// Ustawienia konta -> Zapros znajomych: zaproszenie e-mailem, ranga za
// zaproszenia i mini zawody (ranking miesieczny i ogolny). Dane:
// /api/invites* (backend/blueprints/invites.py).
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Icon from './icons';
import { tx, getLang } from './i18n';

const API = process.env.REACT_APP_API_URL || '';
const STATUS = {
  not_sent: [tx('nie wysłano'), 'acc-badge--warn'],
  sent: [tx('wysłane'), 'acc-badge--muted'],
  joined: [tx('dołączył'), 'acc-badge--info'],
  active: [tx('aktywny'), 'acc-badge--ok'],
};

const fmtDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d) ? '' : d.toLocaleDateString(getLang() === 'en' ? 'en-GB' : 'pl-PL', { day: 'numeric', month: 'short' });
};

function copy(text, notify) {
  navigator.clipboard?.writeText(text).then(() => notify(tx('Skopiowano link zaproszenia')), () => notify(text, 'info'));
}

export default function InvitesPanel({ notify, onOpenRankingSettings }) {
  const [data, setData] = useState(null);
  const [board, setBoard] = useState(null);
  const [period, setPeriod] = useState('month');
  const [email, setEmail] = useState('');
  const [lang, setLang] = useState(getLang());
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [lastLink, setLastLink] = useState(null);

  const load = useCallback(() => {
    axios.get(`${API}/api/invites`).then(r => setData(r.data)).catch(() => setData({ error: true }));
  }, []);
  const loadBoard = useCallback(() => {
    axios.get(`${API}/api/invites/leaderboard`, { params: { period } }).then(r => setBoard(r.data)).catch(() => setBoard(null));
  }, [period]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadBoard(); }, [loadBoard]);

  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/invites`, { email: email.trim(), lang, message });
      if (r.data.email_sent) {
        notify(tx('Zaproszenie wysłane na {0}', [email.trim()]));
        setLastLink(null);
      } else {
        notify(tx('Serwer nie mógł wysłać e-maila — skopiuj link i wyślij go sam.'), 'warning');
        setLastLink(r.data.link);
      }
      setEmail(''); setMessage('');
      load(); loadBoard();
    } catch (err) { notify(err.response?.data?.error || tx('Nie udało się wysłać zaproszenia.'), 'error'); }
    setBusy(false);
  };

  const cancel = async (inv) => {
    if (!window.confirm(tx('Anulować zaproszenie dla {0}?', [inv.email]))) return;
    try { await axios.delete(`${API}/api/invites/${inv.id}`); load(); }
    catch (err) { notify(err.response?.data?.error || tx('Błąd'), 'error'); }
  };

  if (!data) return <div className="empty-mini">{tx('Wczytuję…')}</div>;
  if (data.error) return <div className="empty-mini">{tx('Nie udało się wczytać zaproszeń.')}</div>;
  const rk = data.rank;
  const span = rk.next ? rk.next.points - rk.min : 1;
  const pct = rk.next ? Math.min(100, Math.round(((rk.points - rk.min) / span) * 100)) : 100;
  const rules = data.points_rules;

  return (
    <>
      <section className="acc-section">
        <div className="inv-rank">
          <div className="inv-rank__badge"><Icon name={rk.icon} size={24} /></div>
          <div className="inv-rank__body">
            <div className="inv-rank__head">
              <span className="inv-rank__label">{tx('Twoja ranga')}</span>
              <strong className="inv-rank__title">{rk.title}</strong>
              <span className="inv-rank__lvl">{tx('poziom {0}/{1}', [rk.level, rk.levels])}</span>
            </div>
            <div className="goal__bar" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct} aria-label={tx('Postęp do następnej rangi')}>
              <span style={{ width: `${pct}%` }} />
            </div>
            <div className="inv-rank__foot">
              <span>{tx('{0} pkt', [rk.points])}</span>
              <span>{rk.next ? tx('do rangi „{0}”: {1} pkt', [rk.next.title, rk.next.points - rk.points]) : tx('Najwyższa ranga — gratulacje!')}</span>
            </div>
          </div>
        </div>
        <div className="inv-stats">
          <div><b>{data.counts.sent}</b><span>{tx('wysłanych')}</span></div>
          <div><b>{data.counts.joined}</b><span>{tx('dołączyło')}</span></div>
          <div><b>{data.counts.active}</b><span>{tx('aktywnych')}</span></div>
          <div><b>{data.month_points}</b><span>{tx('pkt w tym miesiącu')}</span></div>
        </div>
        <p className="acc-section__hint">
          {tx('Punkty: +{0} za wysłane zaproszenie (max {1} pkt/mies.), +{2} gdy ktoś założy konto z Twojego linku, +{3} gdy zapisze pierwsze QSO.', [rules.sent, rules.sent_cap * rules.sent, rules.joined, rules.active])}
        </p>
      </section>

      <section className="acc-section">
        <h4 className="acc-section__title"><Icon name="mail" size={16} /> {tx('Zaproś krótkofalowca')}</h4>
        {!data.can_invite ? <p className="acc-alert acc-alert--warn">{data.reason}</p> : (
          <form onSubmit={send}>
            <div className="form-row">
              <div className="form-group form-group--lg">
                <label>{tx('Adres e-mail')}</label>
                <input className="input" type="email" value={email} onChange={e => setEmail(e.target.value)} required
                  placeholder={tx('kolega@example.com')} autoComplete="off" />
              </div>
              <div className="form-group">
                <label>{tx('Język zaproszenia')}</label>
                <select className="input" value={lang} onChange={e => setLang(e.target.value)}>
                  <option value="pl">Polski</option>
                  <option value="en">English</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label>{tx('Kilka słów od siebie (opcjonalnie)')}</label>
              <input className="input" value={message} maxLength={300} onChange={e => setMessage(e.target.value)}
                placeholder={tx('np. Loguję tu wszystkie QSO, spróbuj — 73!')} />
            </div>
            <div className="acc-actions">
              <button type="submit" className="btn btn--primary btn--sm" disabled={busy || !email.trim()}>
                <Icon name="send" size={15} /> {busy ? '…' : tx('Wyślij zaproszenie')}
              </button>
            </div>
            {lastLink && (
              <div className="inv-link">
                <input className="input input--sm" readOnly value={lastLink} onFocus={e => e.target.select()} aria-label={tx('Link zaproszenia')} />
                <button type="button" className="btn btn--ghost btn--sm" onClick={() => copy(lastLink, notify)}><Icon name="copy" size={15} /> {tx('Kopiuj')}</button>
              </div>
            )}
          </form>
        )}
      </section>

      <section className="acc-section">
        <div className="inv-board__head">
          <h4 className="acc-section__title"><Icon name="trophy" size={16} /> {tx('Mini zawody: kto zaprosi najwięcej')}</h4>
          <div className="acc-seg" role="group" aria-label={tx('Okres')}>
            {[['month', tx('Ten miesiąc')], ['all', tx('Od początku')]].map(([k, l]) => (
              <button key={k} type="button" className={'acc-seg__btn' + (period === k ? ' acc-seg__btn--on' : '')} aria-pressed={period === k} onClick={() => setPeriod(k)}>{l}</button>
            ))}
          </div>
        </div>
        {!board ? <div className="empty-mini">{tx('Wczytuję…')}</div> : board.top.length === 0 ? (
          <div className="empty-mini">{period === 'month' ? tx('W tym miesiącu nikt jeszcze nie zdobył punktów — możesz być pierwszy!') : tx('Nikt jeszcze nie zdobył punktów.')}</div>
        ) : (
          <ol className="inv-board">
            {board.top.map(t => (
              <li key={t.callsign} className={'inv-board__row' + (t.me ? ' inv-board__row--me' : '')}>
                <span className={'inv-board__place inv-board__place--' + t.place}>{t.place}</span>
                <span className="inv-board__call">{t.callsign}</span>
                <span className="inv-board__rank">{t.rank}</span>
                <span className="inv-board__pts">{tx('{0} pkt', [t.points])}</span>
              </li>
            ))}
          </ol>
        )}
        {board && (
          <p className="acc-section__hint">
            {board.my_place ? tx('Twoje miejsce: {0} z {1}.', [board.my_place, board.participants]) : tx('Zdobądź pierwsze punkty, żeby pojawić się w rankingu.')}
            {' '}{!board.opted_in && <>{tx('Twój znak widzą inni dopiero po włączeniu udziału w rankingach.')}{' '}
              {onOpenRankingSettings && <button type="button" className="link-btn" onClick={onOpenRankingSettings}>{tx('Włącz w Prywatność i profil')}</button>}</>}
          </p>
        )}
      </section>

      {data.invites.length > 0 && (
        <section className="acc-section">
          <h4 className="acc-section__title">{tx('Wysłane zaproszenia')}</h4>
          <div className="inv-list">
            {data.invites.map(i => {
              const [label, cls] = STATUS[i.status] || STATUS.sent;
              return (
                <div key={i.id} className="inv-list__row">
                  <span className="inv-list__email">{i.invitee_callsign ? <><b>{i.invitee_callsign}</b> · {i.email}</> : i.email}</span>
                  <span className={'acc-badge ' + cls}>{label}</span>
                  <span className="inv-list__date">{fmtDate(i.accepted_at || i.sent_at || i.created_at)}</span>
                  {!i.accepted_at && (
                    <button type="button" className="btn-icon btn-icon--danger" onClick={() => cancel(i)} title={tx('Anuluj')} aria-label={tx('Anuluj')}>
                      <Icon name="x" size={15} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}
    </>
  );
}
